import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
 breadcrumbListSchema,
 collectionPageSchema,
 faqPageSchema,
 jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { serviceFromSlug } from "@/lib/seo-services";
import { CITIES, searchProviders, type Provider } from "@/data/heallexa";
import { citySlug, specialtySlug } from "@/lib/seo-slugs";
import { ProviderCard } from "@/components/search/ProviderCard";

const SITE = "https://heallexa.com";

function resolveCity(slug: string) {
 const lower = slug.toLowerCase();
 const found = CITIES.find((c) => citySlug(c) === lower);
 if (!found) return null;
 const [city, st] = found.split(",").map((x) => x.trim());
 return { city, state: st, full: found };
}

export const Route = createFileRoute("/services/$service/$citySlug")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const svc = serviceFromSlug(params.service);
 const loc = resolveCity(params.citySlug);
 if (!svc || !loc) throw notFound();
 const all: Provider[] = [];
 for (const spec of svc.specialties.slice(0, 3)) {
 try {
 const rows = await searchProviders({ specialty: spec, location: loc.full, limit: 10 });
 all.push(...rows);
 } catch {
 // ignore
 }
 }
 const dedup = Array.from(new Map(all.map((p) => [p.id, p])).values()).slice(0, 20);
 // Soft 404 fix: a 0-provider service+city combo used to render a 200
 // with "no providers listed yet" filler. Google treats that as a soft
 // 404 and keeps re-crawling it. A real 404 lets it deindex cleanly; the
 // combo can be re-crawled once matching providers exist in that city.
 if (dedup.length === 0) throw notFound();
 return { svc, ...loc, providers: dedup };
 },
 head: ({ params, loaderData }) => {
 if (!loaderData) return {};
 const { svc, city, state, providers } = loaderData;
 const title = `${svc.name} in ${city}, ${state} | Heallexa`;
 const description = `Find providers in ${city}, ${state} offering ${svc.name.toLowerCase()}. ${svc.intro}`;
 const url = `${SITE}/services/${svc.slug}/${params.citySlug}`;
 const decision = decideIndexability({
 providerCount: providers.length,
 hasFaq: true,
 hasNearbyCities: true,
 hasRelatedProviders: providers.length > 0,
 internalLinkCount: 20,
 });
 return {
 meta: [
 { title },
 { name: "description", content: description },
 robotsMeta(decision.robots),
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "article" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 jsonLdScript(collectionPageSchema({ name: title, description, url })),
 jsonLdScript(
 breadcrumbListSchema([
 { name: "Home", url: "/" },
 { name: "Services", url: "/services" },
 { name: svc.name, url: `/services/${svc.slug}` },
 { name: city, url },
 ]),
 ),
 jsonLdScript(
 faqPageSchema([
 { q: `Who offers ${svc.name.toLowerCase()} in ${city}?`, a: `${svc.specialties.join(", ")} in ${city}, ${state} commonly provide ${svc.name.toLowerCase()}.` },
 { q: `Can I book ${svc.name.toLowerCase()} online in ${city}?`, a: `Many ${city} providers support online booking. Check the listings above.` },
 ]),
 ),
 ],
 };
 },
 component: ServiceCityPage,
 notFoundComponent: () => (
 <div className="min-h-screen flex items-center justify-center">
 <div className="text-center">
 <h1 className="text-2xl font-semibold mb-2">Page not found</h1>
 <Link to="/browse" className="text-primary underline">Browse providers</Link>
 </div>
 </div>
 ),
});

function ServiceCityPage() {
 const { svc, city, state, providers, full } = Route.useLoaderData();
 const cs = citySlug(full);

 const specialtyLinks = svc.specialties.map((s: string) => ({
 label: `${s} offering ${svc.name.toLowerCase()} in ${city}`,
 href: `/services/${svc.slug}/${specialtySlug(s)}/${cs}`,
 }));

 const nearbyCities = CITIES.filter((c) => c.endsWith(`, ${state}`) && c.split(",")[0].trim() !== city).slice(0, 8).map((c) => ({
 label: `${svc.name} in ${c.replace(`, ${state}`, "")}`,
 href: `/services/${svc.slug}/${citySlug(c)}`,
 }));

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <Breadcrumbs
 className="mb-6"
 items={[
 { name: "Home", href: "/" },
 { name: "Services", href: "/services" },
 { name: svc.name, href: `/services/${svc.slug}` },
 { name: city },
 ]}
 />
 <header className="mb-8">
 <p className="text-[12px] uppercase tracking-[0.14em] text-muted-foreground">
 {providers.length} providers found
 </p>
 <h1 className="font-display text-[28px] font-semibold tracking-tight text-foreground mt-2">
 {svc.name} in {city}, {state}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-2xl">{svc.intro}</p>
 </header>

 <section className="mb-10 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
 Educational only, not medical advice. Always consult a qualified healthcare provider.
 </section>

 <section className="mb-16">
 <h2 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground mb-8">
 {providers.length} providers in {city}
 </h2>
 <ul className="grid gap-5">
 {providers.map((p: any) => (
 <li key={p.id}><ProviderCard p={p} /></li>
 ))}
 </ul>
 </section>

 <Reveal from="up" delay={60}>
 <SeoLinkList heading={`Refine ${svc.name} in ${city} by specialty`} links={specialtyLinks} columns={3} className="mb-10" />
 </Reveal>
 {nearbyCities.length > 0 && (
 <Reveal from="up" delay={60}>
 <SeoLinkList heading="Nearby cities" links={nearbyCities} columns={3} icon={MapPin} className="mb-10" />
 </Reveal>
 )}

 <SeoFaq
 className="mb-8"
 items={[
 { q: `How much does ${svc.name.toLowerCase()} cost in ${city}?`, a: `Cost varies by provider and insurance. Ask the office for an estimate before booking.` },
 { q: `Do these providers accept new patients?`, a: `Most listings indicate availability. Confirm with the office.` },
 ]}
 />
 </main>
 <FooterSection />
 </div>
 );
}
